<template>
  <ZubieDialog
    :content-class="!$q.screen.gt.sm ? 'q-pa-none' : undefined"
    :max-width="!$q.screen.gt.sm ? '100%' : undefined"
    :maximized="!$q.screen.gt.sm"
    ok-only
    :visible="visible"
    @before-hide="onBeforeHide"
    @show="onShow"
  >
    <div class="container column justify-end relative full-height bg-black">
      <video ref="video" class="video full-height full-width" :class="{ 'video--shown': videoInitialized }"></video>
      <div
        v-if="scanMode === 'text'"
        class="preview full-height full-width absolute bg-white"
        :class="{ 'preview--shown': scanned }"
      >
        <canvas ref="preview" class="canvas full-height full-width"></canvas>
      </div>
      <canvas ref="targets" class="canvas full-height full-width absolute"></canvas>
      <transition name="fade">
        <ScanHelpCard v-if="showScanHelpCard" class="absolute-center" @done="onScanHelpDone">
          Position the text you want to scan in the center of your camera and click "Scan" when you're ready.
        </ScanHelpCard>
      </transition>
      <transition name="fade">
        <ScanHelpCard v-if="showResultHelpCard" class="absolute-center" @done="onResultHelpDone">
          Choose a highlighted area to use the text in that area.
        </ScanHelpCard>
      </transition>
      <transition name="fade">
        <ScanHelpCard
          v-if="showNoResultsCard"
          class="absolute-top full-width text-brown-10 bg-warning"
          dense
          @done="showNoResultsCard = false"
        >
          We were unable to identify text in the photo, please try again.
        </ScanHelpCard>
      </transition>
      <q-spinner-grid v-if="isProcessing" class="absolute-center" color="primary" size="4em" />
    </div>
    <template #actions>
      <div class="row justify-between items-center no-wrap full-width">
        <div>
          <q-btn-toggle
            v-model="scanMode"
            class="q-ml-sm"
            dense
            :options="[
              { label: `QR/Bar${$q.screen.gt.xs ? ' Code' : ''}`, value: 'code' },
              { label: 'Text', value: 'text' },
            ]"
            :padding="$q.screen.gt.xs ? 'sm md' : 'xs sm'"
            square
            toggle-color="accent"
            @update:model-value="onScanModeChange"
          />
        </div>
        <div v-if="scanMode === 'text'">
          <q-btn
            class="q-px-sm"
            :class="{ 'full-width': !$q.screen.gt.sm }"
            color="primary"
            :icon="scanned ? 'restart_alt' : 'photo_camera'"
            :label="scanned ? ($q.screen.gt.xs ? 'Try Again' : 'Redo') : 'Scan'"
            square
            @click.stop="onCameraButtonClick"
          />
        </div>
        <div>
          <CancelButton class="q-px-sm" :class="{ 'full-width': !$q.screen.gt.sm }" label="Cancel" @click.stop="hide" />
        </div>
      </div>
    </template>
  </ZubieDialog>
</template>
<script>
import { Dialog } from 'quasar';
import { createWorker, PSM } from 'tesseract.js/dist/tesseract.min.js';
import AlertDialog from 'components/AlertDialog.vue';
import ScanHelpCard from 'components/scanner/ScanHelpCard.vue';
import errorService from 'src/services/error';
import { setTimeoutPromise } from 'src/services/setTimeout';
import storage from 'src/services/storage';
import { ocr } from 'src/services/web-ocr';

const CODE_SCAN_PAUSE_MS = 250;

export default {
  name: 'ScanDialog',
  props: {
    defaultScanMode: {
      type: String,
      default: 'code',
    },
    validation: String,
  },
  components: {
    ScanHelpCard,
  },
  data() {
    return {
      isProcessing: false,
      mediaStream: null,
      ocrWorker: null,
      scanned: false,
      scanMode: this.defaultScanMode,
      showNoResultsCard: false,
      showResultHelpCard: false,
      showScanHelpCard: false,
      symbols: [],
      videoInitialized: false,
      visible: true,
    };
  },
  methods: {
    /**
     * @param {CanvasRenderingContext2D} context
     * @param {number} width
     * @param {number} height
     */
    async barQrCodeScan(imageCanvas, width, height) {
      const imageDataUrl = imageCanvas.toDataURL();
      const imageData = imageCanvas.getContext('2d').getImageData(0, 0, width, height);

      const symbols = await window.zbarWasm.scanImageData(imageData);
      if (symbols.length) {
        await setTimeoutPromise(500);
        let text = symbols[0].decode();
        this.finishScan(imageDataUrl, [text]);
      }
    },
    clearMatches() {
      const { height, width } = this.$refs.targets;
      const canvasContext = this.$refs.targets.getContext('2d');
      canvasContext.clearRect(0, 0, width, height);
    },
    /**
     * @param {number} height
     * @param {number} width
     */
    ensureCanvasSize(height, width) {
      if (this.$refs.targets.height !== height || this.$refs.targets.width !== width) {
        this.$refs.targets.height = height;
        this.$refs.targets.width = width;
      }
    },
    /**
     * @param {{ text: string; valid: boolean; }} payload
     */
    finishScan(imageDataUrl, textBlocks) {
      this.$emit('ok', imageDataUrl, textBlocks);
      this.hide();
    },
    hide() {
      this.visible = false;
    },
    /**
     * @param {HTMLCanvasElement} imageCanvas
     * @param {number} width
     * @param {number} height
     */
    async ocrScan(imageCanvas, width, height) {
      const imageDataUrl = imageCanvas.toDataURL();
      const imageData = imageCanvas.getContext('2d').getImageData(0, 0, width, height);

      this.isProcessing = true;
      const scanResults = await ocr(this.ocrWorker, imageData);
      this.isProcessing = false;

      try {
        const symbols = [];
        scanResults.forEach((line) => {
          const { bbox, text } = line;
          symbols.push({
            bbox,
            rect: [bbox.x0, bbox.y0, bbox.x1 - bbox.x0, bbox.y1 - bbox.y0],
            text,
          });
        });
        this.finishScan(
          imageDataUrl,
          scanResults.map(({ text }) => text)
        );
        // this.outlineMatches(symbols);
        // this.showResultHelpCard = scanResults.length && storage.get('scan-dialog/showResultHelpCard') !== 'false';
        // const matches = text.match(VIN_REGEX);
        // if (matches) {
        //   const vin = matches[0];
        //   if (validateVin(vin)) {
        //     this.finishScan(vin);
        //   }
        // }
      } catch {
        // do nothing
      }

      if (!scanResults.length) {
        this.showNoResultsCard = true;
        this.scanned = false;
      }
    },
    onBeforeHide() {
      clearInterval(this.interval);
      document.body.classList.remove('scan-dialog--open');
    },
    async onCameraButtonClick() {
      this.showNoResultsCard = false;

      if (this.scanned) {
        this.scanned = false;
        this.clearMatches();
      } else {
        this.scanned = true;
        await this.scan();
      }
    },
    onResultHelpDone() {
      this.showResultHelpCard = false;
      storage.set('scan-dialog/showResultHelpCard', false);
    },
    onScanHelpDone() {
      this.showScanHelpCard = false;
      storage.set('scan-dialog/showScanHelpCard', false);
    },
    async onScanModeChange() {
      if (this.scanMode === 'text' && this.ocrWorker === null) {
        this.ocrWorker = await createWorker('eng');
        this.ocrWorker.setParameters({
          tessedit_char_whitelist: '01234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ ',
          tessedit_pageseg_mode: PSM.SPARSE_TEXT,
        });
      }

      this.symbols = [];
      this.scanned = false;

      if (this.scanMode === 'code') {
        this.scan(); // start continuous scanning process
      } else {
        // Show help card if it hasn't been shown
        this.showScanHelpCard = storage.get('scan-dialog/showScanHelpCard') !== 'false';
      }
    },
    onShow() {
      document.body.classList.add('scan-dialog--open');
    },
    outlineMatches(symbols) {
      this.clearMatches();

      const canvasContext = this.$refs.targets.getContext('2d');
      canvasContext.strokeStyle = '#00ff00';
      canvasContext.lineWidth = 3;

      this.symbols = symbols.map((symbol) => {
        const { points, rect } = symbol;

        if (rect) {
          const path = new Path2D();
          path.rect(...rect);
          canvasContext.stroke(path);
          return {
            ...symbol,
            path,
          };
        }

        const minMax = {
          xMin: null,
          xMax: null,
          yMin: null,
          yMax: null,
        };
        points.forEach((point) => {
          const { x, y } = point;
          if (x < minMax.xMin || minMax.xMin === null) {
            minMax.xMin = x;
          }
          if (x > minMax.xMax || minMax.xMax === null) {
            minMax.xMax = x;
          }
          if (y < minMax.yMin || minMax.yMin === null) {
            minMax.yMin = y;
          }
          if (y > minMax.yMax || minMax.yMax === null) {
            minMax.yMax = y;
          }
        });
        const box = [
          { x: minMax.xMin, y: minMax.yMax },
          { x: minMax.xMax, y: minMax.yMax },
          { x: minMax.xMax, y: minMax.yMin },
          { x: minMax.xMin, y: minMax.yMin },
        ];

        canvasContext.beginPath();
        box.forEach((point, index) => {
          const { x, y } = point;
          if (index === 0) {
            canvasContext.moveTo(x, y);
          } else {
            canvasContext.lineTo(x, y);
          }
        });
        canvasContext.closePath();
        canvasContext.stroke();

        return symbol;
      });
    },
    async scan() {
      const video = this.$refs.video;

      if (!video) {
        return;
      }

      const { videoHeight, videoWidth } = video;

      if (videoHeight > 0 && videoWidth > 0) {
        this.ensureCanvasSize(videoHeight, videoWidth);

        // Create a canvas from the video frame
        const imageCanvas = this.scanMode === 'code' ? document.createElement('canvas') : this.$refs.preview;
        imageCanvas.height = videoHeight;
        imageCanvas.width = videoWidth;

        // Draw a video frame
        const canvasContext = imageCanvas.getContext('2d');
        canvasContext.drawImage(video, 0, 0, videoWidth, videoHeight);

        if (this.scanMode === 'code') {
          await this.barQrCodeScan(imageCanvas, videoWidth, videoHeight);
        } else {
          await this.ocrScan(imageCanvas, videoWidth, videoHeight);
        }
      }

      if (this.scanMode === 'code') {
        // Continuous scan
        await setTimeoutPromise(CODE_SCAN_PAUSE_MS);
        // Make sure we're still in "code" mode after pause
        if (this.scanMode === 'code') {
          this.scan();
        }
      }
    },
    // setScanArea() {
    //   // TODO: remove this if it's useless
    //   const { videoHeight: height, videoWidth: width } = this.$refs.video || {};

    //   this.$refs.scanArea.height = height;
    //   this.$refs.scanArea.width = width;

    //   const scanAreaContext = this.$refs.scanArea.getContext('2d');
    //   scanAreaContext.strokeStyle = '#333333';
    //   scanAreaContext.lineWidth = 3;
    //   scanAreaContext.clearRect(0, 0, width, height);

    //   // Determine constraints
    //   this.scanWidthConstraint = width * (2 / 3);
    //   this.scanOffsetLeft = width * (1 / 6);
    //   this.scanHeightConstraint = height * (2 / 5);
    //   this.scanOffsetTop = (height - this.scanHeightConstraint) / 2;

    //   // Create cover
    //   scanAreaContext.fillStyle = 'rgba(0, 0, 0, 0.5)';
    //   scanAreaContext.fillRect(0, 0, width, this.scanOffsetTop); // top
    //   scanAreaContext.fillRect(0, this.scanOffsetTop, this.scanOffsetLeft, this.scanHeightConstraint); // left
    //   scanAreaContext.fillRect(
    //     this.scanWidthConstraint + this.scanOffsetLeft,
    //     this.scanOffsetTop,
    //     this.scanOffsetLeft,
    //     this.scanHeightConstraint
    //   ); // right
    //   scanAreaContext.fillRect(0, this.scanOffsetTop + this.scanHeightConstraint, width, this.scanOffsetTop); // bottom
    // },
  },
  async created() {
    this.scanner = window.zbarWasm.getDefaultScanner();
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'environment',
          focusMode: 'continuous',
        },
      });
      this.$refs.video.srcObject = this.mediaStream;
      this.$refs.video.setAttribute('playsinline', true); // iOS hack
      this.$refs.video.play();

      const doneInitializing = () => {
        this.videoInitialized = true;
        this.$refs.video.removeEventListener('playing', doneInitializing);
      };
      this.$refs.video.addEventListener('playing', doneInitializing);

      this.onScanModeChange();
    } catch (error) {
      errorService.log(error);

      let errorMessage = "An unexpected error occurred when attempting to access your device's camera.";
      if (error.message.includes('denied')) {
        errorMessage = "Please allow access to your device's camera and try again.";
      }

      Dialog.create({
        component: AlertDialog,
        componentProps: {
          message: errorMessage,
          titleColor: 'negative',
        },
      }).onDismiss(() => {
        this.hide();
      });
    }
  },
  async unmounted() {
    await this.ocrWorker?.terminate();
  },
};
</script>

<style lang="scss" scoped>
.video {
  object-fit: cover;
  opacity: 0;
}

.video--shown {
  opacity: 1;
}

.canvas {
  object-fit: cover;
}

.preview {
  opacity: 0;

  .canvas {
    opacity: 0;
    transition: opacity 0.3s ease-in;
  }
}

.preview--shown {
  opacity: 1;

  .canvas {
    opacity: 1;
  }
}

.fade-leave-active {
  transition: opacity 0.5s;
  opacity: 0;
}

.fade-leave,
.fade-enter-to {
  opacity: 1;
}
</style>

<style lang="scss">
body.scan-dialog--open .q-dialog__inner > div {
  max-height: 100% !important;
}
</style>
